import { useState, useCallback } from 'react';
import Storage from 'storage';

const STORAGE_KEY = 'open_quotes';

export interface OpenQuoteInfo {
  id: number;
  line_of_business: number;
  rfq_ref?: string;
}

interface OpenQuotesState {
  contactRef: string;
  quotes: OpenQuoteInfo[];
}

const getStoredState = (): OpenQuotesState | null => {
  return Storage.session.get(STORAGE_KEY);
};

const setStoredState = (state: OpenQuotesState | null) => {
  if (state) {
    Storage.session.store(STORAGE_KEY, state);
  } else {
    Storage.session.remove(STORAGE_KEY);
  }
};

export const useOpenQuotes = (contactRef: string) => {
  const [quotes, setQuotesState] = useState<OpenQuoteInfo[]>(() => {
    const stored = getStoredState();
    if (stored && stored.contactRef === contactRef) {
      return stored.quotes;
    }
    return [];
  });

  const setQuotes = useCallback(
    (newQuotes: OpenQuoteInfo[]) => {
      setQuotesState(newQuotes);
      if (newQuotes.length > 0) {
        setStoredState({ contactRef, quotes: newQuotes });
      } else {
        setStoredState(null);
      }
    },
    [contactRef],
  );

  const removeQuote = useCallback(
    (id: number) => {
      setQuotesState((prev) => {
        const updated = prev.filter((q) => q.id !== id);
        if (updated.length > 0) {
          setStoredState({ contactRef, quotes: updated });
        } else {
          setStoredState(null);
        }
        return updated;
      });
    },
    [contactRef],
  );

  const clearQuotes = useCallback(() => {
    setQuotesState([]);
    setStoredState(null);
  }, []);

  return {
    quotes,
    setQuotes,
    removeQuote,
    clearQuotes,
  };
};

export default useOpenQuotes;
